/**
 * Constantes métier de la brique logistique, SANS aucune dépendance runtime (ni vine, ni Lucid) :
 * ce module est exporté par le barrel (`index.ts`), donc embarqué par Metro dans le bundle Expo.
 * Toute règle de validation (qui, elle, importe vine) reste dans `validation/logistics.ts`, en
 * deep-import uniquement.
 *
 * Point de vérité unique des énumérations du module : les modèles Lucid, les schémas Vine, le
 * contrat du solveur et le back-office s'y réfèrent plutôt que de recopier les littéraux.
 */

import { sortWeekDays, type WeekDay, weekDays } from './enums/week-days.js'
import type { StoreProjectStatus, StoreStatus } from './index.js'
import { STORE_PROJECT_API_STATUS_OF, type StoreProjectApiStatus } from './store-project-fields.js'

/**
 * `logistics_vehicles.vehicle_type`, du plus petit au plus grand gabarit : le solveur et le résumé
 * des scénarios actifs lisent cet ordre, par `compareLogisticsVehicleTypes`.
 */
export const LOGISTICS_VEHICLE_TYPES = ['VL', 'PL', 'SL'] as const
export type LogisticsVehicleType = (typeof LOGISTICS_VEHICLE_TYPES)[number]

/** Comparateur du plus petit au plus grand gabarit ; inverser les arguments pour l'ordre inverse. */
export const compareLogisticsVehicleTypes = (
  a: LogisticsVehicleType,
  b: LogisticsVehicleType,
): number => LOGISTICS_VEHICLE_TYPES.indexOf(a) - LOGISTICS_VEHICLE_TYPES.indexOf(b)

/**
 * Un référentiel admet ce type (types acceptés d'une boutique, entrants d'un hub) : `null` = aucune
 * restriction déclarée, une liste vide n'en admet aucun. Même lecture que le solveur
 * (`toVehicleTypes`, puis `storesAcceptType` / `hubAdmitsInbound`).
 */
export const logisticsAcceptsVehicleType = (
  types: readonly string[] | null,
  type: LogisticsVehicleType,
): boolean => types === null || types.includes(type)

/**
 * Types admis en approche (atelier → hub), par ordre de préférence du solveur : le hub doit en
 * accepter au moins un en entrée (`hub_type_not_allowed` sinon).
 */
export const LOGISTICS_APPROACH_VEHICLE_TYPES = [
  'SL',
  'PL',
] as const satisfies readonly LogisticsVehicleType[]

/** Objet indexé par type de véhicule, chaque valeur calculée pour son type (cf. `mapWeekDays`). */
export const mapVehicleTypes = <T>(
  valueOf: (type: LogisticsVehicleType) => T,
): Record<LogisticsVehicleType, T> =>
  Object.fromEntries(LOGISTICS_VEHICLE_TYPES.map((type) => [type, valueOf(type)])) as Record<
    LogisticsVehicleType,
    T
  >

/**
 * Normalisation d'une plaque (brief : « plate (normalisée, unique) ») : majuscules, séparateurs
 * (espaces, tirets, points) supprimés. `AB-123-CD`, `ab 123 cd` et `AB.123.CD` normalisent tous
 * en `AB123CD`. Seule règle d'une plaque valide, partagée par l'écriture (`LogisticsVehicle`),
 * la validation de l'API et la fiche du BO : une plaque qui normalise en chaîne vide est refusée.
 */
export const normalizeLogisticsPlate = (plate: string): string =>
  plate.toUpperCase().replace(/[^A-Z0-9]/g, '')

/**
 * Gabarit routier d'un camion (`logistics_vehicles`) : clés, dans l'ordre de la signature du cache
 * des trajets. Seule liste, reprise par le routage, la validation, l'effacement au changement de
 * type et la fiche du BO.
 */
export const LOGISTICS_ROAD_PARAMETER_KEYS = [
  'totalPermittedWeightKg',
  'heightCm',
  'lengthCm',
  'widthCm',
  'axleCount',
] as const

/**
 * Tracé routier d'un parcours ordonné (`POST /logistics/routing/route`), calculé à la demande pour
 * une carte, assemblé à partir de tronçons conservés en base jusqu'à leur expiration (LU-166).
 * Plafond d'étapes d'un appel fournisseur pour les tronçons manquants.
 */
export const LOGISTICS_ROUTE_MAX_WAYPOINTS = 30

/** Borne HTTP du lot ; le serveur découpe ensuite en appels PTV de 30 étapes. */
export const LOGISTICS_ROUTE_MAX_BATCH_ROUTES = 100

/**
 * Arrêts au plus d'un aperçu d'ordre (`POST /logistics/simulations/order-preview`), boutiques et
 * projets ensemble : leur matrice lit le cache, avec des durées approchées pour les arcs absents,
 * sans appel au fournisseur (LU-205).
 * Bien au-delà d'une tournée d'un camion ; au-delà, la modale ne demande rien et garde l'ordre du
 * choix, la route refuse (422).
 */
export const LOGISTICS_ORDER_PREVIEW_MAX_STOPS = 50

/**
 * Décimales gardées sur les coordonnées routées : 5, environ un mètre. Le serveur y arrondit les
 * étapes avant l'appel et la clé de ses caches ; la carte y compare deux étapes consécutives.
 */
export const LOGISTICS_ROUTE_COORDINATE_DECIMALS = 5

/**
 * Coordonnée arrondie à `LOGISTICS_ROUTE_COORDINATE_DECIMALS` : étapes envoyées au routeur et clés
 * des caches du serveur, corps des tracés du back-office. Un même lieu y tombe sur le même point,
 * donc sur la même entrée de cache, des deux côtés.
 */
export const roundRouteCoordinate = (value: number) =>
  Number(value.toFixed(LOGISTICS_ROUTE_COORDINATE_DECIMALS))

/** Ligne GeoJSON d'un tronçon, en `[longitude, latitude]` comme le veut GeoJSON. */
export interface LogisticsRouteLineString {
  type: 'LineString'
  coordinates: [number, number][]
}

/** Tronçon entre deux étapes consécutives du parcours. */
export interface LogisticsRouteLeg {
  distanceMeters: number
  durationSeconds: number
  /**
   * Le tronçon emprunte une route interdite ou restreinte au véhicule (PTV `violated`) : il suit
   * les routes, mais pas des routes praticables par ce gabarit.
   */
  restricted: boolean
  geometry: LogisticsRouteLineString
}

/**
 * Pourquoi un parcours n'a pas de tracé. La carte n'en dessine alors aucun : jamais de segment
 * droit à la place de la route.
 * - `no_provider` : pas de routeur réel (`fixture`, le défaut), ou pas de clé ;
 * - `provider_error` : le routeur a échoué (panne, quota, requête refusée) ;
 * - `no_route` : le routeur répond, mais sans route entre ces étapes.
 */
export const LOGISTICS_ROUTE_UNAVAILABLE_REASONS = [
  'no_provider',
  'provider_error',
  'no_route',
] as const
export type LogisticsRouteUnavailableReason = (typeof LOGISTICS_ROUTE_UNAVAILABLE_REASONS)[number]

/**
 * Distances et durées de référence, sans heure de départ : celles qui font foi pour une tournée
 * restent celles des matrices horaires.
 */
export type LogisticsRoute =
  | {
      status: 'ok'
      distanceMeters: number
      durationSeconds: number
      /** Un tronçon par couple d'étapes consécutives, dans l'ordre du parcours. */
      legs: LogisticsRouteLeg[]
    }
  | { status: 'unavailable'; reason: LogisticsRouteUnavailableReason }

/**
 * Mise en forme d'un nom interne de camion, quelle que soit la couche (écriture, API, fiche) :
 * Unicode NFC (l'index `LOWER(internal_name)` ne confondrait pas un « é » composé et décomposé),
 * espaces rognés et ramenés à un seul (« PL  01 » et « PL 01 » sont le même camion).
 */
export const normalizeLogisticsInternalName = (name: string): string =>
  name.normalize('NFC').trim().replace(/\s+/g, ' ')

/**
 * Libellés d'affichage des types, communs à tout le module : `SL` (tracteur et semi-remorque) se
 * dit « SPL » (Super Poids Lourd, le permis qui le conduit) dans le métier et les maquettes ; le
 * code stocké reste `SL`.
 */
export const LOGISTICS_VEHICLE_TYPE_LABELS: Record<
  LogisticsVehicleType,
  { short: string; long: string }
> = {
  VL: { short: 'VL', long: 'Véhicule léger (VL)' },
  PL: { short: 'PL', long: 'Poids lourd (PL)' },
  SL: { short: 'SPL', long: 'Super Poids Lourd (SPL)' },
}

/**
 * `store_logistics_profiles.allowed_vehicle_types` : sous-ensemble strict des types de véhicule.
 * `SL` (semi-remorque, « SPL ») n'en fait jamais partie — aucune boutique ne se livre en semi.
 */
export const LOGISTICS_STORE_VEHICLE_TYPES = ['VL', 'PL'] as const
export type LogisticsStoreVehicleType = (typeof LOGISTICS_STORE_VEHICLE_TYPES)[number]

/** `logistics_driver_licences.category` : permis B/C/CE. */
export const LOGISTICS_LICENCE_CATEGORIES = ['B', 'C', 'CE'] as const
export type LogisticsLicenceCategory = (typeof LOGISTICS_LICENCE_CATEGORIES)[number]

/**
 * Libellés des permis, ceux de la fiche chauffeur (maquette du ticket #899), sur tous les écrans :
 * la catégorie et le gabarit qu'elle conduit (`LOGISTICS_LICENCE_BY_VEHICLE_TYPE`). Le SL s'y dit
 * « Semi » ; le type de véhicule garde « SPL » (`LOGISTICS_VEHICLE_TYPE_LABELS`).
 */
export const LOGISTICS_LICENCE_CATEGORY_LABELS: Record<LogisticsLicenceCategory, string> = {
  B: `B - ${LOGISTICS_VEHICLE_TYPE_LABELS.VL.short}`,
  C: `C - ${LOGISTICS_VEHICLE_TYPE_LABELS.PL.short}`,
  CE: 'CE - Semi',
}

/**
 * Permis qui conduit chaque gabarit : B → VL, C → PL, CE → SL. Seule source de cette
 * correspondance, lue par le solveur (`canDrive`) et par les pièces exigées du poids lourd.
 */
export const LOGISTICS_LICENCE_BY_VEHICLE_TYPE: Record<
  LogisticsVehicleType,
  LogisticsLicenceCategory
> = {
  VL: 'B',
  PL: 'C',
  SL: 'CE',
}

/**
 * `logistics_driver_licences.kind` : nature d'une pièce de chauffeur (#899). La table garde le nom
 * qu'elle avait quand elle ne portait que des permis ; seul un permis (`licence`) a une catégorie.
 */
export const LOGISTICS_DRIVER_DOCUMENT_KINDS = [
  'identity_card',
  'licence',
  'medical_certificate',
  'driver_card',
  'fco',
  'dpae',
  'other',
] as const
export type LogisticsDriverDocumentKind = (typeof LOGISTICS_DRIVER_DOCUMENT_KINDS)[number]

/**
 * Pièce qu'un chauffeur doit fournir : un permis nomme sa catégorie, aucune autre pièce n'en a
 * (CHECK `logistics_driver_licences_kind_category`).
 */
export type LogisticsRequiredDriverDocument =
  | { kind: 'licence'; category: LogisticsLicenceCategory }
  | { kind: Exclude<LogisticsDriverDocumentKind, 'licence' | 'other'>; category: null }

/** Libellés des pièces, ceux de la fiche chauffeur (maquette du ticket #899). */
export const LOGISTICS_DRIVER_DOCUMENT_KIND_LABELS: Record<LogisticsDriverDocumentKind, string> = {
  identity_card: 'Carte nationale d’identité',
  licence: 'Permis',
  medical_certificate: 'Attestation médicale',
  driver_card: 'Carte conducteur',
  fco: 'FCO',
  dpae: 'DPAE',
  other: 'Autres documents',
}

/**
 * `logistics_driver_licences.status` : vérification d'une pièce du chauffeur. Le tableau est la
 * source du type, comme pour les autres énumérations de ce fichier.
 */
export const LOGISTICS_DRIVER_DOCUMENT_STATUSES = ['pending', 'verified', 'rejected'] as const
export type LogisticsDriverDocumentStatus = (typeof LOGISTICS_DRIVER_DOCUMENT_STATUSES)[number]

/** Statut d'une pièce, en minuscules après son libellé (« Permis B — à vérifier »). */
export const LOGISTICS_DRIVER_DOCUMENT_STATUS_LABELS: Record<
  LogisticsDriverDocumentStatus,
  string
> = {
  pending: 'à vérifier',
  verified: 'vérifiée',
  rejected: 'refusée',
}

/** Ce qui désigne une pièce : sa nature, et sa catégorie pour un permis (`null` sinon). */
export interface LogisticsDriverDocumentIdentity {
  kind: LogisticsDriverDocumentKind
  category: LogisticsLicenceCategory | null
}

/**
 * « Permis C » pour un permis, le libellé de sa nature pour toute autre pièce. Accepte une pièce
 * telle que l'API la rend, catégorie typée nullable : un permis sans catégorie (exclu par le CHECK)
 * se nommerait « Permis ».
 */
export const driverDocumentLabel = (document: LogisticsDriverDocumentIdentity): string =>
  document.kind === 'licence' && document.category !== null
    ? `Permis ${document.category}`
    : LOGISTICS_DRIVER_DOCUMENT_KIND_LABELS[document.kind]

/** Permis du poids lourd : ceux de tout gabarit autre que le VL. */
const HEAVY_VEHICLE_LICENCE_CATEGORIES: ReadonlySet<LogisticsLicenceCategory> = new Set(
  LOGISTICS_VEHICLE_TYPES.filter((type) => type !== 'VL').map(
    (type) => LOGISTICS_LICENCE_BY_VEHICLE_TYPE[type],
  ),
)

/** Pièces que le poids lourd ajoute, dans l'ordre de la fiche chauffeur. */
const HEAVY_VEHICLE_DOCUMENT_KINDS = ['medical_certificate', 'driver_card', 'fco'] as const

const HEAVY_VEHICLE_DOCUMENT_KIND_SET: ReadonlySet<LogisticsDriverDocumentKind> = new Set(
  HEAVY_VEHICLE_DOCUMENT_KINDS,
)

/**
 * Une pièce se dépose-t-elle avec sa date de fin de validité ? Oui pour celles du poids lourd :
 * attestation médicale, carte conducteur et FCO, toutes à échéance réglementaire, et permis C ou
 * CE, limité à cinq ans et seul relu par la simulation. Sans date, une fois vérifiées, elles
 * vaudraient pour toujours. Facultative pour un permis B (ancien modèle papier sans date), une
 * carte d'identité (validité parfois prolongée au-delà de la date imprimée) et une DPAE (sans fin).
 */
export const driverDocumentRequiresValidUntil = (
  document: LogisticsDriverDocumentIdentity,
): boolean =>
  document.kind === 'licence'
    ? document.category !== null && HEAVY_VEHICLE_LICENCE_CATEGORIES.has(document.category)
    : HEAVY_VEHICLE_DOCUMENT_KIND_SET.has(document.kind)

/**
 * Fin de validité la plus lointaine acceptée au dépôt d'une pièce qui en exige une
 * (`driverDocumentRequiresValidUntil`), en années à partir d'aujourd'hui : garde-fou contre une
 * faute de frappe (2203 pour 2031), aucune de ces pièces ne valant plus de cinq ans. La date bornée
 * (`logisticsMaxDriverDocumentValidUntil`, `logistics-dates.ts`) et le message de refus sont communs
 * à l'API et au futur formulaire de la fiche.
 */
export const LOGISTICS_DRIVER_DOCUMENT_MAX_VALIDITY_YEARS = 10

/** Refus d'une fin de validité au-delà de `LOGISTICS_DRIVER_DOCUMENT_MAX_VALIDITY_YEARS`. */
export const driverDocumentValidUntilTooFarMessage = (
  document: LogisticsDriverDocumentIdentity,
): string =>
  `La date de fin de validité de la pièce « ${driverDocumentLabel(document)} » ne peut pas dépasser ${LOGISTICS_DRIVER_DOCUMENT_MAX_VALIDITY_YEARS} ans à partir d’aujourd’hui.`

/** Refus d'une pièce à échéance (`driverDocumentRequiresValidUntil`) déposée sans date de fin. */
export const driverDocumentValidUntilRequiredMessage = (
  document: LogisticsDriverDocumentIdentity,
): string => `Précisez la date de fin de validité de la pièce « ${driverDocumentLabel(document)} ».`

/** Formats d'une pièce chauffeur déposée (`POST /logistics/drivers/:id/licences`). */
export const LOGISTICS_DRIVER_DOCUMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'] as const

/** Les mêmes formats, tels qu'on les nomme à l'écran (`jpeg` est un `jpg`). */
export const LOGISTICS_DRIVER_DOCUMENT_FORMATS_LABEL = 'PDF, JPG ou PNG'

/** Taille d'une pièce chauffeur déposée, en Mo. */
export const LOGISTICS_DRIVER_DOCUMENT_MAX_MEGABYTES = 20

/** Refus d'une pièce déposée sans fichier ou trop lourde, à l'écran comme dans l'API. */
export const LOGISTICS_DRIVER_DOCUMENT_FILE_REQUIRED_MESSAGE = 'Choisissez un fichier.'
export const LOGISTICS_DRIVER_DOCUMENT_SIZE_MESSAGE = `Le fichier dépasse ${LOGISTICS_DRIVER_DOCUMENT_MAX_MEGABYTES} Mo.`

/**
 * Pièces obligatoires d'un chauffeur selon ses permis déclarés (#899), dans l'ordre de la fiche.
 *
 * PROVISOIRE : la liste complète est « à fournir par Lucie » (ticket Notion #899). D'ici là, la
 * règle donnée par Saad le 21/09/2026 (`docs/superpowers/journal/2026-09-21-brique-logistique-decisions-saad.md`) :
 * carte nationale d'identité, un permis par catégorie déclarée et DPAE pour tous ; attestation
 * médicale, carte conducteur et FCO seulement avec un permis C ou CE. « Autres documents » n'est
 * jamais exigé ; la photo citée ce jour-là n'est ni dans le ticket ni dans la maquette.
 */
export const requiredDriverDocuments = (
  licenceCategories: readonly LogisticsLicenceCategory[],
): LogisticsRequiredDriverDocument[] => {
  const declared = LOGISTICS_LICENCE_CATEGORIES.filter((category) =>
    licenceCategories.includes(category),
  )
  const drivesHeavyVehicles = declared.some((category) =>
    HEAVY_VEHICLE_LICENCE_CATEGORIES.has(category),
  )
  return [
    { kind: 'identity_card', category: null },
    ...declared.map((category) => ({ kind: 'licence' as const, category })),
    ...(drivesHeavyVehicles
      ? HEAVY_VEHICLE_DOCUMENT_KINDS.map((kind) => ({ kind, category: null }))
      : []),
    { kind: 'dpae', category: null },
  ]
}

/** `logistics_driver_profiles.employment_type`. */
export const LOGISTICS_EMPLOYMENT_TYPES = ['internal', 'contractor'] as const
export type LogisticsEmploymentType = (typeof LOGISTICS_EMPLOYMENT_TYPES)[number]

/** Libellés du statut d'un chauffeur, ceux de la fiche chauffeur (maquette du ticket #899). */
export const LOGISTICS_EMPLOYMENT_TYPE_LABELS: Record<LogisticsEmploymentType, string> = {
  internal: 'Salarié Lucie',
  contractor: 'Sous-traitant',
}

export const isLogisticsEmploymentType = (value: unknown): value is LogisticsEmploymentType =>
  (LOGISTICS_EMPLOYMENT_TYPES as readonly unknown[]).includes(value)

/** Refus d'un profil chauffeur créé sans statut (salarié ou sous-traitant). */
export const LOGISTICS_DRIVER_EMPLOYMENT_TYPE_REQUIRED_MESSAGE = 'Précisez le statut du chauffeur.'

/** Refus d'un chauffeur sous-traitant (`contractor`) sans transporteur. */
export const LOGISTICS_DRIVER_CARRIER_REQUIRED_MESSAGE =
  'Choisissez le transporteur de ce chauffeur.'

/** Refus d'un profil chauffeur sans permis : il en déclare toujours au moins un. */
export const LOGISTICS_DRIVER_LICENCE_REQUIRED_MESSAGE =
  'Déclarez au moins une catégorie de permis.'

/** `store_logistics_profiles.delivery_mode`. */
export const LOGISTICS_DELIVERY_MODES = ['autonomous', 'assisted'] as const
export type LogisticsDeliveryMode = (typeof LOGISTICS_DELIVERY_MODES)[number]

/**
 * `logistics_simulations.kind` : type de tournée. `last_mile` = livraison directe atelier →
 * boutiques, `approach` = acheminement jusqu'à un hub de rupture de charge. `full` (les deux flux
 * combinés, ticket #847) n'est plus créé depuis #902 : acheminement et dernier km sont deux
 * tournées indépendantes (`LOGISTICS_SIMULATION_CREATE_KINDS`), mais les simulations `full` déjà
 * enregistrées restent lisibles. Le moteur ne lit pas `kind`, mais `kind` contraint `hubIds`
 * (`logisticsHubIdsForKindError`, `validation/logistics.ts`) : `last_mile` sans hub,
 * `approach`/`full` avec exactement un hub — et c'est `hubIds` qui ouvre au moteur la famille via
 * hub, qu'il compare à la livraison directe. Un `last_mile` part de l'atelier, ou d'un hub de
 * l'atelier (`departureHubId`, LU-196, `logisticsDepartureHubForKindError`). Un `approach` s'arrête
 * au hub, sans boutique (LU-197, `logisticsStoresForKindError`) : il y porte ce qu'en repartent
 * livrer les derniers km en vigueur, et roule le jour même, sans tournée de nuit (LU-212,
 * `logisticsApproachScheduleError`).
 */
export const LOGISTICS_SIMULATION_KINDS = ['last_mile', 'approach', 'full'] as const
export type LogisticsSimulationKind = (typeof LOGISTICS_SIMULATION_KINDS)[number]

/** Types qu'une création ou un changement de type accepte (#902) : jamais `full`. */
export const LOGISTICS_SIMULATION_CREATE_KINDS = ['last_mile', 'approach'] as const
export type LogisticsSimulationCreateKind = (typeof LOGISTICS_SIMULATION_CREATE_KINDS)[number]

/** Types d'arrêt d'une tournée : une boutique ou une boutique en projet (#941). */
export const LOGISTICS_SIMULATION_STOP_TYPES = ['store', 'store_project'] as const
export type LogisticsSimulationStopType = (typeof LOGISTICS_SIMULATION_STOP_TYPES)[number]

/** Type d'un lieu d'une tournée : l'atelier, un hub ou un arrêt (contrat du solveur, tracés du BO). */
export type LogisticsPlaceType = 'central_kitchen' | 'hub' | LogisticsSimulationStopType

/** Type d'un arrêt de livraison : ni l'atelier, ni le hub. */
export const isLogisticsSimulationStopType = (
  value: string,
): value is LogisticsSimulationStopType =>
  (LOGISTICS_SIMULATION_STOP_TYPES as readonly string[]).includes(value)

/**
 * Ordre de passage d'un scénario (LU-161) : `laden`, le moins de kilomètres en charge, celui de sa
 * famille et le seul recommandable ; `cost` ou `duration`, une variante d'ordre gardée parce
 * qu'elle est la moins chère ou la plus rapide de son run.
 */
export const LOGISTICS_SCENARIO_ORDERS = ['laden', 'cost', 'duration'] as const
export type LogisticsScenarioOrder = (typeof LOGISTICS_SCENARIO_ORDERS)[number]

/** Ce que comparent les étiquettes « Le moins cher » et « Le plus rapide ». */
export interface LogisticsScenarioMeasures {
  /** Coût total, `null` s'il est inconnu. */
  totalCents: number | null
  totalDurationSeconds: number
}

/** Inconnu = pire. `∞ − ∞` vaut `NaN`, faux : le critère suivant départage. */
const byKnownCost = (a: LogisticsScenarioMeasures, b: LogisticsScenarioMeasures): number =>
  (a.totalCents ?? Number.POSITIVE_INFINITY) - (b.totalCents ?? Number.POSITIVE_INFINITY)

const byDuration = (a: LogisticsScenarioMeasures, b: LogisticsScenarioMeasures): number =>
  a.totalDurationSeconds - b.totalDurationSeconds

/**
 * Le moins cher (coût connu, puis le plus court) et le plus rapide (puis le moins cher) des
 * scénarios d'un run (LU-161) ; `undefined` sans candidat (sans coût connu, pour le moins cher).
 * À égalité parfaite, le premier dans l'ordre de `candidates` : les ordres des familles, dans
 * l'ordre des familles, puis les variantes. Seule copie de la règle : le moteur s'en sert pour
 * choisir les variantes à garder (`winningOrderVariants`), la modale Alternatives pour ses étiquettes
 * (`labelAlternatives`).
 */
export const cheapestAndFastestOf = <T>(
  candidates: readonly T[],
  measuresOf: (candidate: T) => LogisticsScenarioMeasures,
): { cheapest: T | undefined; fastest: T | undefined } => {
  const measured = candidates.map((candidate) => ({ candidate, measures: measuresOf(candidate) }))
  const [cheapest] = measured
    .filter((entry) => entry.measures.totalCents !== null)
    .sort((a, b) => byKnownCost(a.measures, b.measures) || byDuration(a.measures, b.measures))
  const [fastest] = [...measured].sort(
    (a, b) => byDuration(a.measures, b.measures) || byKnownCost(a.measures, b.measures),
  )
  return { cheapest: cheapest?.candidate, fastest: fastest?.candidate }
}

/** Rattachement d'un véhicule ou d'un collaborateur logistique : un atelier OU un hub. */
export interface LogisticsHome {
  homeCentralKitchenId: number | null
  homeHubId: number | null
}

/** Rattachement à exactement un atelier OU un hub (CHECK `*_single_home` / `*_role_home` en base). */
export const hasExactlyOneHome = (home: LogisticsHome): boolean =>
  (home.homeCentralKitchenId !== null) !== (home.homeHubId !== null)

// --- Paramètres de simulation -----------------------------------------------------------

/**
 * Paramètres de simulation logistique qui ont une valeur par défaut, surchargeable tournée par
 * tournée. Les valeurs du réseau (supports, poids) n'en font pas partie : `LogisticsNetworkParameters`.
 */
export const LOGISTICS_DEFAULT_PARAMETERS = {
  /** Toutes les boutiques livrées avant cette heure locale. */
  deliveryDeadline: '10:30',
  /** Durée d'un arrêt boutique : 20 min. */
  stopDurationSeconds: 1200,
  /** Rupture de charge sur un hub : 45 min. À confirmer par Saad. */
  hubTransferDurationSeconds: 2700,
  /**
   * Conduite continue maximale avant pause obligatoire : 4 h 30. Confirmé par Saad le 21/09/2026,
   * qui corrigeait la maquette — « c'est quatre heures et demie avec une pause de 45 minutes,
   * encore 4 h 30 avec une pause de 45 minutes », pour 9 h de conduite au total.
   */
  maxContinuousDrivingMinutes: 270,
  /** Pause obligatoire après la conduite continue maximale : 45 min. Confirmé le 21/09/2026. */
  mandatoryBreakMinutes: 45,
  /** Vol d'oiseau → route, adaptateur de dev uniquement (quand aucune matrice mesurée). */
  detourFactor: 1.3,
} as const

export type LogisticsDefaultedParameter = keyof typeof LOGISTICS_DEFAULT_PARAMETERS

/**
 * Emplacements de pali occupés par une palette : une palette 800×1200 couvre deux palis 800×600.
 *
 * L'unité de charge est le SUPPORT, palette ou pali — décision du 21/09/2026 : « on a charge utile,
 * palettes, palis, c'est ça qui décidera des commandes de demain ». Le rapport, saisi tournée par
 * tournée jusqu'au 28/10/2026, est une constante du réseau depuis (LU-160).
 */
export const LOGISTICS_PALIS_PER_PALLET = 2

/**
 * Valeurs du réseau que lit le calcul, une seule source pour toutes les tournées (LU-160, décision
 * de Léo du 28/10/2026) : le rapport palette → palis est une constante, les poids des supports des
 * paramètres globaux (`global_params`, édités avec les caisses par pali du réappro). Plus de coût
 * carburant : le coût au km du camion est son coût complet. Les simulations antérieures gardent
 * leurs anciennes saisies dans `parameters`, que le calcul ne lit plus.
 */
export interface LogisticsNetworkParameters {
  palisPerPallet: number
  palletWeightKg: number
  paliWeightKg: number
}

/**
 * Sans défaut ET jamais requis : leur absence n'est pas une donnée manquante, c'est l'absence de
 * contrainte (ticket #855, onglet Contraintes). Contrôlés par service à la fin de l'ordonnancement
 * (`route_duration_exceeded` / `route_distance_exceeded`, solveur).
 */
export const LOGISTICS_OPTIONAL_PARAMETERS = [
  'maxRouteDurationSeconds',
  'maxRouteDistanceMeters',
  'vehicleIds',
] as const

export type LogisticsOptionalParameter = (typeof LOGISTICS_OPTIONAL_PARAMETERS)[number]

/**
 * Heure `HH:MM` → secondes depuis minuit : seule conversion des heures d'une tournée, lue par le
 * moteur (`solver/common.ts`) et par le BO (frise des alternatives).
 */
export const logisticsTimeToSeconds = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 3600 + minutes * 60
}

/** Un jour, en secondes : au-delà, un instant d'une tournée tombe le lendemain de son départ. */
export const LOGISTICS_DAY_SECONDS = 86_400

/**
 * `HH:MM` d'une heure lue LE JOUR D'ARRIVÉE (limites de livraison et de retour, créneaux des
 * boutiques et des hubs) → secondes depuis le minuit du jour de départ. `arrivalOffsetSeconds` : 0
 * le jour même, `LOGISTICS_DAY_SECONDS` pour une tournée de nuit (#903). Même règle pour le moteur
 * (`solver/common.ts`) et la frise des alternatives.
 */
export const logisticsArrivalTimeToSeconds = (time: string, arrivalOffsetSeconds: number): number =>
  logisticsTimeToSeconds(time) + arrivalOffsetSeconds

/** Secondes depuis minuit → `HH:MM` (inverse de `logisticsTimeToSeconds`, au-delà de minuit : `24:30`). */
export const logisticsSecondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Horizon maximal de la date d'effet d'une bascule en opérationnel (#860) : la validation remplace
 * AUSSITÔT les tournées en vigueur des boutiques communes, une date lointaine (faute de frappe,
 * 2062 pour 2026) les ferait tomber aujourd'hui pour une remplaçante qui n'entrerait en vigueur que
 * dans des années. Borne commune à l'API (VineJS) et au formulaire du BO (Zod).
 */
export const LOGISTICS_MAX_EFFECTIVE_FROM_YEARS = 1
/** Fuseau des dates d'exploitation (date d'effet, « aujourd'hui ») : le même côté API et BO. */
export const LOGISTICS_BUSINESS_ZONE = 'Europe/Paris'
export const LOGISTICS_EFFECTIVE_FROM_TOO_FAR_MESSAGE = `La date de prise d’effet ne peut pas dépasser ${LOGISTICS_MAX_EFFECTIVE_FROM_YEARS} an${LOGISTICS_MAX_EFFECTIVE_FROM_YEARS > 1 ? 's' : ''} à partir d’aujourd’hui.`

/** Ce que le moteur et le BO lisent d'un véhicule pour juger s'il peut servir une tournée. */
export interface LogisticsVehicleEligibility {
  isActive: boolean
  pallets800x1200: number | null
  palis800x600: number | null
  homeCentralKitchenId: number | null
  homeHubId: number | null
}

/** Au moins un contenant déclaré (palettes ou palis) : sans quoi le véhicule ne transporte rien. */
export const logisticsVehicleHasCapacity = (vehicle: LogisticsVehicleEligibility): boolean =>
  (vehicle.pallets800x1200 ?? 0) > 0 || (vehicle.palis800x600 ?? 0) > 0

/**
 * Capacité en EMPLACEMENTS DE PALI, l'unité commune du calcul : un véhicule déclare la même surface
 * en palettes et en palis, on garde le maximum des deux expressions converties (ce qui couvre aussi
 * un véhicule qui n'en déclare qu'une). Seule définition, lue par le moteur (`resolveVehicles`) et
 * par le détail d'une tournée.
 */
export const logisticsPaliSlotCapacity = (
  vehicle: Pick<LogisticsVehicleEligibility, 'pallets800x1200' | 'palis800x600'>,
  palisPerPallet: number,
): number => Math.max((vehicle.pallets800x1200 ?? 0) * palisPerPallet, vehicle.palis800x600 ?? 0)

/**
 * Rattaché à l'atelier de la tournée ou à l'un de ses hubs ; au départ d'un hub (LU-196), à ce
 * seul hub. Côté base, le même périmètre est la scope `LogisticsVehicle.inPerimeter`, ou le hub de
 * départ (`vehiclesOutsidePerimeterError`, `resolveVehicles`).
 */
export const logisticsVehicleInPerimeter = (
  vehicle: LogisticsVehicleEligibility,
  centralKitchenId: number,
  hubIds: readonly number[],
  departureHubId: number | null = null,
): boolean =>
  departureHubId === null
    ? vehicle.homeCentralKitchenId === centralKitchenId ||
      (vehicle.homeHubId !== null && hubIds.includes(vehicle.homeHubId))
    : vehicle.homeHubId === departureHubId

/**
 * Camion que le moteur peut mobiliser pour une tournée : actif, avec un contenant, rattaché à
 * l'atelier ou à l'un de ses hubs. Seule définition côté BO (création, onglet Contraintes) ; côté
 * base, la même règle est portée par `resolveVehicles`.
 */
export const logisticsVehicleMobilizable = (
  vehicle: LogisticsVehicleEligibility,
  centralKitchenId: number,
  hubIds: readonly number[],
  departureHubId: number | null = null,
): boolean =>
  vehicle.isActive &&
  logisticsVehicleHasCapacity(vehicle) &&
  logisticsVehicleInPerimeter(vehicle, centralKitchenId, hubIds, departureHubId)

/**
 * Jour d'arrivée d'une tournée (#903), par rapport à son jour de départ : le jour même (0) ou le
 * lendemain (1), pour une tournée partie le soir. Les horaires sont les mêmes tous les jours
 * d'activité : un seul décalage vaut pour chaque départ.
 */
export type LogisticsArrivalDayOffset = 0 | 1

/**
 * Jour d'arrivée déduit des horaires (#935) : un retour antérieur au départ au cadran tombe le
 * lendemain (départ 22 h, retour 10 h), sinon le jour même. Seul le retour en décide : une limite
 * de livraison antérieure au départ, avec un retour le jour même, est refusée à la saisie
 * (`logisticsDeliveryDeadlineError`), pas lue comme une tournée de nuit. Les deux heures au format
 * `HH:MM` : la comparaison lexicographique suffit. Écrit par le serveur à chaque enregistrement des
 * horaires, relu par le BO pour ses aides.
 */
export const logisticsArrivalDayOffsetOf = (
  departureTime: string,
  returnDeadline: string,
): LogisticsArrivalDayOffset => (returnDeadline < departureTime ? 1 : 0)

/**
 * Heure limite de livraison appliquée par le calcul (#935) : celle de la tournée, sinon son
 * paramètre, sinon le défaut du moteur (`LOGISTICS_DEFAULT_PARAMETERS`). Seule définition, lue par
 * les validateurs de la tournée et `buildProblem` côté serveur, par l'onglet Contraintes et la
 * frise des alternatives côté BO.
 */
export const logisticsEffectiveDeliveryDeadlineOf = (
  deliveryDeadline: string | null | undefined,
  parameters: Pick<LogisticsParametersOverrides, 'deliveryDeadline'> | null | undefined,
): string =>
  deliveryDeadline ?? parameters?.deliveryDeadline ?? LOGISTICS_DEFAULT_PARAMETERS.deliveryDeadline

/**
 * Refus d'une limite de livraison APPLIQUÉE qui ne suit pas le départ d'une tournée de jour (#935) :
 * celle d'une tournée qui n'en a pas de propre (paramètre ou défaut, que l'onglet Contraintes
 * affiche dans le champ), et le contrôle du solveur, qui ne sait pas d'où elle vient. L'heure est
 * nommée, puisque l'utilisateur ne l'a pas forcément saisie. Ici et non dans `validation/logistics.ts`
 * avec les autres messages des horaires : le dossier `solver/` n'importe aucun module Vine.
 */
export const logisticsAppliedDeliveryBeforeDepartureMessage = (deliveryDeadline: string): string =>
  `L'heure limite de livraison appliquée (${deliveryDeadline}) ne suit pas l'heure de départ : aucune boutique ne peut être livrée à temps. Saisissez une heure limite de livraison après le départ, ou avancez le départ.`

/**
 * Jours d'arrivée à destination, dans l'ordre de la semaine : les jours de départ décalés de
 * `offset` (dimanche soir → lundi). Lus par le moteur (jours livrés et créneaux des boutiques et
 * des hubs d'une tournée de nuit) comme par le BO (aide sous les jours d'activité).
 */
export const logisticsArrivalDaysOf = (
  days: readonly WeekDay[],
  offset: LogisticsArrivalDayOffset,
): WeekDay[] =>
  sortWeekDays(days.map((day) => weekDays[(weekDays.indexOf(day) + offset) % weekDays.length]!))

/** Paramètres tels qu'ils sortent de la résolution : défauts garantis, limites optionnelles. */
export interface LogisticsParameters {
  deliveryDeadline: string
  stopDurationSeconds: number
  hubTransferDurationSeconds: number
  maxContinuousDrivingMinutes: number
  mandatoryBreakMinutes: number
  detourFactor: number
  /** Temps de trajet maximal d'un service (secondes), sans défaut : pas de contrainte si absent. */
  maxRouteDurationSeconds?: number
  /** Kilométrage maximal d'un service (mètres), sans défaut : pas de contrainte si absent. */
  maxRouteDistanceMeters?: number
  /**
   * Camion de la tournée (`logistics_vehicles.id`) : un seul à l'écriture (#901,
   * `logisticsVehicleIdsError`, appliquée par les contrôleurs et non par `parametersField`). Absent
   * ou vide : le simulateur choisit (#936) le plus petit camion de l'atelier qui suffit. Les
   * simulations antérieures se relisent telles quelles, plusieurs camions compris (« Camions
   * mobilisables », ticket #855) : le calcul n'en retient qu'un. Un id présent ici mais non
   * mobilisable (inactif, autre rattachement) reste exclu par `resolveVehicles` : ce paramètre
   * RESTREINT la flotte candidate, il ne l'étend jamais.
   */
  vehicleIds?: number[]
}

/** Surcharges stockées sur `logistics_simulations.parameters` : tout est optionnel. */
export type LogisticsParametersOverrides = Partial<LogisticsParameters>

/**
 * Catégories des boutiques sur la carte du simulateur, celles du foncier : les boutiques ouvertes,
 * puis les dossiers en cours par statut de projet, dans l'ordre de la carte « Dossiers en cours »
 * du dashboard. `closed` range ce que le foncier ne montre pas : une boutique fermée
 * définitivement ou supprimée, ou un projet abandonné, qu'une tournée peut encore desservir.
 */
export const LOGISTICS_STORE_MAP_CATEGORIES = [
  'open',
  'openable',
  'works',
  'secured',
  'advanced_project',
  'under_study',
  'before_project',
  'closed',
] as const satisfies readonly (StoreProjectApiStatus | 'closed')[]

export type LogisticsStoreMapCategory = (typeof LOGISTICS_STORE_MAP_CATEGORIES)[number]

/** Projet du foncier tel que la carte le lit. */
interface MapStoreProject {
  status: StoreProjectStatus
  isAbandoned: boolean
}

/** Catégorie d'un projet du foncier sur la carte (`undefined` : projet disparu de la base). */
export const logisticsStoreProjectMapCategoryOf = (
  project: MapStoreProject | undefined,
): LogisticsStoreMapCategory =>
  project === undefined || project.isAbandoned
    ? 'closed'
    : STORE_PROJECT_API_STATUS_OF[project.status]

/**
 * Catégorie d'une boutique sur la carte : le statut de son projet du foncier, comme le dashboard.
 * Sans projet (ou projet abandonné), son propre statut en tient lieu : ouverte ou fermée
 * temporairement, elle compte comme ouverte ; en stockage ou en préparation, comme ouvrable. Une
 * boutique fermée définitivement ou supprimée (`undefined` : disparue de la base) est fermée, quel
 * que soit son projet.
 */
export const logisticsStoreMapCategoryOf = (
  store: { status: StoreStatus; isDeleted: boolean } | undefined,
  project: MapStoreProject | null,
): LogisticsStoreMapCategory => {
  if (store === undefined || store.isDeleted || store.status === 'PERMANENTLY_CLOSED')
    return 'closed'
  if (project !== null && !project.isAbandoned) return STORE_PROJECT_API_STATUS_OF[project.status]
  return store.status === 'STOCKING' || store.status === 'PREPARING' ? 'openable' : 'open'
}

/** Chiffres minimum du numéro de tournée : « T-001 », puis « T-1000 » sans troncature. */
export const LOGISTICS_TOUR_NUMBER_DIGITS = 3

/**
 * Nom d'une simulation et de ses tournées (#942, format provisoire de la maquette V3 décidé par
 * Léo le 16/10) : « T-057 · Osaka 2 », numéro attribué par le serveur à la création de la
 * simulation, puis libellé de l'atelier courant. Jamais stocké : il suit un changement d'atelier
 * ou son renommage. Sans numéro (avant la création, aperçu du BO) : « T-··· · Osaka 2 ».
 * Recopié en SQL par `logisticsNameSql` (backend) pour les recherches : les modifier ensemble.
 */
export const logisticsTourName = (number: number | null, centralKitchenLabel: string): string =>
  `T-${number === null ? '···' : String(number).padStart(LOGISTICS_TOUR_NUMBER_DIGITS, '0')} · ${centralKitchenLabel}`

/**
 * Point de départ d'un scénario, lu dans ses tracés (origine, arrêts puis destination de chaque
 * segment) : l'atelier s'il y figure, même comme arrêt (un acheminement, dont les camions du hub
 * repartent du hub), sinon le premier lieu du premier tracé, le hub d'un dernier km parti d'un hub
 * (LU-196). `null` sans tracé. Seule copie de la règle : le backend (`hubCount` du scénario actif)
 * et le BO (pastille et sous-titres) la lisent tous deux.
 */
export const logisticsDepartureOfPaths = <P extends { type: LogisticsPlaceType }>(
  paths: readonly (readonly P[])[],
): P | null =>
  paths.flat().find((place) => place.type === 'central_kitchen') ?? paths[0]?.[0] ?? null
